/**
 * A PROJECT or PICKUP block rendered as a bar on the dispatch grid (A6-2) — the
 * non-task counterpart of TaskBar. The parent positions it via CSS grid; this is
 * presentational only (the draggable wrapper owns click vs drag). Status-coloured
 * like a task bar, with a briefcase (project) / truck (pickup) accent.
 */
import { Briefcase, Star, Truck } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Covers project + pickup statuses; unknowns fall back to the scheduled look. */
const STATUS_CLASSES: Record<string, string> = {
  scheduled: 'bg-status-scheduled text-status-scheduled-fg border-status-scheduled-fg/20',
  en_route: 'bg-status-enroute text-status-enroute-fg border-status-enroute-fg/30',
  on_site: 'bg-status-onsite text-status-onsite-fg border-status-onsite-fg/30',
  in_progress: 'bg-status-onsite text-status-onsite-fg border-status-onsite-fg/40',
  punch: 'bg-status-onsite text-status-onsite-fg border-status-onsite-fg/30',
  pending_closeout: 'bg-status-complete text-status-complete-fg border-status-complete-fg/30',
  complete: 'bg-status-complete text-status-complete-fg border-status-complete-fg/30',
  blocked: 'bg-status-blocked text-status-blocked-fg border-status-blocked-fg/40',
  cancelled: 'bg-fill-soft text-ink-faint border-line line-through',
};

export function BlockBar({
  kind,
  title,
  subtitle,
  status,
  isLead = false,
  style,
}: {
  kind: 'project' | 'pickup';
  title: string;
  subtitle?: string;
  status: string;
  isLead?: boolean;
  style?: React.CSSProperties;
}) {
  const Icon = kind === 'pickup' ? Truck : Briefcase;
  return (
    <div
      style={style}
      className={cn(
        'block px-2 py-1.5 rounded-md border text-xs leading-tight overflow-hidden select-none',
        'hover:shadow-sm transition-shadow',
        STATUS_CLASSES[status] ?? STATUS_CLASSES.scheduled,
      )}
      title={`${title}${subtitle ? ' · ' + subtitle : ''}`}
    >
      <div className="flex items-center gap-1 font-semibold truncate">
        <Icon className="w-3 h-3 flex-shrink-0 opacity-70" />
        <span className="truncate">{title}</span>
        {isLead && <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-500 flex-shrink-0" />}
      </div>
      {subtitle && <div className="text-[10px] opacity-80 truncate">{subtitle}</div>}
    </div>
  );
}
